import dayjs from "dayjs"
import { useCallback, useEffect, useReducer, useRef } from "react"

import {
    DnoApplicationTypes,
    DnoEscalationStatuses,
} from "../../../../../constants/enums"
import {
    APPLICATION_PAGE_SIZE,
    ASSESSMENT_TYPE_KW,
    STATUS,
    APPLICATION_ID_KW,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    UPDATED_SINCE,
    UPDATED_TO,
    TECHNOLOGY,
    ESCALATED_REASON,
    INSTALLER_EMAIL,
    POSTCODE,
    ADDRESS,
    ASSIGNED_TO,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    MIN_MAX_DEMAND,
    MAX_MAX_DEMAND,
    IS_UNREAD,
} from "../../../../../constants/generalConstants"
import { postClientProtected } from "../../../clients/clients"
import {
    IApplicationSummary,
    IApplications,
    dnoApplicationsMapper,
} from "../../../mappers/dnoInterface/dnoApplicationsMapper"
import {
    DNO_LIST_APPLICATIONS,
    DNO_LIST_APPLICATIONS_MONTHLY,
} from "../../../urls/hybrid"

// ── Types ──────────────────────────────────────────────────────────────────

export interface MonthBucket {
    /** Key like "2026-05" */
    key: string
    /** Display label like "May 2026" */
    label: string
    /** ISO start of month */
    from: string
    /** ISO end of range (end of month or now for current month) */
    to: string
    data: IApplicationSummary[]
    downloadData: unknown[]
    /** Cursor for next page within month; null = no more pages */
    nextPageKey: Record<string, string> | null
    /** All pages within this month have been fetched */
    isComplete: boolean
    isLoading: boolean
    isError: boolean
}

type State = {
    buckets: MonthBucket[]
    /** Whether the initial auto-fill sequence is running */
    isAutoFilling: boolean
}

type Action =
    | { type: "ADD_MONTH"; bucket: MonthBucket }
    | {
          type: "FETCH_START"
          monthKey: string
      }
    | {
          type: "FETCH_SUCCESS"
          monthKey: string
          data: IApplicationSummary[]
          downloadData: unknown[]
          nextPageKey: Record<string, string> | null
      }
    | { type: "FETCH_ERROR"; monthKey: string }
    | { type: "SET_AUTO_FILLING"; value: boolean }
    | { type: "RESET"; initialBucket: MonthBucket }
    | { type: "RESET_FILTERED"; initialBucket: MonthBucket }
    | {
          type: "FETCH_SUCCESS_FILTERED"
          monthKey: string
          data: IApplicationSummary[]
          downloadData: unknown[]
          nextPageKey: Record<string, string> | null
      }

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "ADD_MONTH": {
            // Don't add if already exists
            if (state.buckets.some((b) => b.key === action.bucket.key)) {
                return state
            }
            return {
                ...state,
                buckets: [...state.buckets, action.bucket],
            }
        }
        case "FETCH_START":
            return {
                ...state,
                buckets: state.buckets.map((b) =>
                    b.key === action.monthKey
                        ? { ...b, isLoading: true, isError: false }
                        : b
                ),
            }
        case "FETCH_SUCCESS": {
            const hasMorePages = action.nextPageKey !== null
            return {
                ...state,
                buckets: state.buckets.map((b) =>
                    b.key === action.monthKey
                        ? {
                              ...b,
                              data: [...b.data, ...action.data],
                              downloadData: [
                                  ...b.downloadData,
                                  ...action.downloadData,
                              ],
                              nextPageKey: action.nextPageKey,
                              isComplete: !hasMorePages,
                              isLoading: false,
                          }
                        : b
                ),
            }
        }
        case "FETCH_ERROR":
            return {
                ...state,
                buckets: state.buckets.map((b) =>
                    b.key === action.monthKey
                        ? { ...b, isLoading: false, isError: true }
                        : b
                ),
            }
        case "SET_AUTO_FILLING":
            return { ...state, isAutoFilling: action.value }
        case "RESET":
            return {
                buckets: [action.initialBucket],
                isAutoFilling: true,
            }
        case "RESET_FILTERED":
            return {
                buckets: [action.initialBucket],
                isAutoFilling: false,
            }
        case "FETCH_SUCCESS_FILTERED": {
            const hasMorePages = action.nextPageKey !== null
            // Group data by modifiedEpochTime month
            const grouped = new Map<
                string,
                { data: IApplicationSummary[]; downloadData: unknown[] }
            >()
            action.data.forEach((item, idx) => {
                let monthKey: string
                if (
                    item.modifiedEpochTime !== undefined &&
                    typeof item.modifiedEpochTime === "number"
                ) {
                    monthKey = dayjs
                        .unix(item.modifiedEpochTime)
                        .format("YYYY-MM")
                } else {
                    monthKey = action.monthKey // fallback
                }
                if (!grouped.has(monthKey)) {
                    grouped.set(monthKey, { data: [], downloadData: [] })
                }
                const group = grouped.get(monthKey)!
                group.data.push(item)
                group.downloadData.push(action.downloadData[idx])
            })

            // Merge grouped data into existing buckets or create new ones
            let updatedBuckets = [...state.buckets]
            grouped.forEach((group, key) => {
                const existingIdx = updatedBuckets.findIndex(
                    (b) => b.key === key
                )
                if (existingIdx >= 0) {
                    const existing = updatedBuckets[existingIdx]
                    updatedBuckets[existingIdx] = {
                        ...existing,
                        data: [...existing.data, ...group.data],
                        downloadData: [
                            ...existing.downloadData,
                            ...group.downloadData,
                        ],
                        nextPageKey: action.nextPageKey,
                        isComplete: !hasMorePages,
                        isLoading: false,
                    }
                } else {
                    const monthStart = dayjs(`${key}-01`)
                    updatedBuckets.push({
                        key,
                        label: monthStart.format("MMMM YYYY"),
                        from: monthStart.format("YYYY-MM-DD"),
                        to: monthStart.endOf("month").format("YYYY-MM-DD"),
                        data: group.data,
                        downloadData: group.downloadData,
                        nextPageKey: action.nextPageKey,
                        isComplete: !hasMorePages,
                        isLoading: false,
                        isError: false,
                    })
                }
            })

            // Mark the original fetch bucket as done if it got no data.
            // Always mark complete — pagination continues via the buckets
            // that received data (they retain nextPageKey).
            updatedBuckets = updatedBuckets.map((b) => {
                if (
                    b.key === action.monthKey &&
                    !grouped.has(action.monthKey)
                ) {
                    return {
                        ...b,
                        isComplete: true,
                        isLoading: false,
                    }
                }
                return b
            })

            // Sort buckets newest-first
            updatedBuckets.sort((a, b) => (b.key > a.key ? 1 : -1))

            return {
                ...state,
                buckets: updatedBuckets,
            }
        }
        default:
            return state
    }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function createMonthBucket(
    monthStart: dayjs.Dayjs,
    isCurrentMonth: boolean
): MonthBucket {
    return {
        key: monthStart.format("YYYY-MM"),
        label: monthStart.format("MMMM YYYY"),
        from: monthStart.format("YYYY-MM-DD"),
        to: isCurrentMonth
            ? dayjs()
                  .endOf("month")
                  .hour(23)
                  .minute(59)
                  .second(59)
                  .format("YYYY-MM-DDTHH:mm:ss")
            : monthStart
                  .endOf("month")
                  .hour(23)
                  .minute(59)
                  .second(59)
                  .format("YYYY-MM-DDTHH:mm:ss"),
        data: [],
        downloadData: [],
        nextPageKey: null,
        isComplete: false,
        isLoading: false,
        isError: false,
    }
}

function getPreviousMonth(monthKey: string): dayjs.Dayjs {
    return dayjs(`${monthKey}-01`).subtract(1, "month")
}

/** Maximum consecutive empty months before stopping backward expansion */
const MAX_EMPTY_MONTHS = 24

/** User-set filter param keys — when any are present, skip auto-fill / backfill */
const USER_FILTER_KEYS = [
    APPLICATION_ID_KW,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    UPDATED_SINCE,
    UPDATED_TO,
    STATUS,
    TECHNOLOGY,
    ESCALATED_REASON,
    INSTALLER_EMAIL,
    POSTCODE,
    ADDRESS,
    ASSIGNED_TO,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    MIN_MAX_DEMAND,
    MAX_MAX_DEMAND,
    IS_UNREAD,
]

// ── Hook ───────────────────────────────────────────────────────────────────

export type TGetDnoApplicationsMonthly = {
    monthBuckets: MonthBucket[]
    allData: IApplicationSummary[]
    downloadData: { results: unknown[] }
    isLoadingAny: boolean
    isAutoFilling: boolean
    isError: boolean
    isFinalMonth: boolean
    loadMonth: (monthKey: string) => void
    loadMore: () => void
    reset: () => void
}

const useGetDnoApplicationsMonthly = (
    searchParams: URLSearchParams,
    isEscalations: boolean,
    implicitStatuses?: string[],
    enabled = true
): TGetDnoApplicationsMonthly => {
    // Build search params for the API (assessment type + default statuses)
    const searchParamsLocal = new URLSearchParams(searchParams.toString())
    searchParamsLocal.set(
        ASSESSMENT_TYPE_KW,
        isEscalations
            ? DnoApplicationTypes.allIncludingCx
            : DnoApplicationTypes.allApprovedIncCx
    )

    // Override pre-defined or implicit statuses for this request if the user has defined one in search/URL.

    // If none are set by the user, statuses here are added to the API URL but NOT counted as user
    // filters, so the monthly date-range bucketing (updatedSince/updatedTo) is
    // preserved.

    if (isEscalations && !searchParams.has(STATUS)) {
        Object.values(DnoEscalationStatuses).forEach((s) => {
            searchParamsLocal.append(STATUS, s)
        })
    }
    if (implicitStatuses?.length && !searchParams.has(STATUS)) {
        implicitStatuses.forEach((s) => searchParamsLocal.append(STATUS, s))
    }

    const searchParamsString = searchParamsLocal.toString()

    // Detect whether the user has any active filter params
    const hasUserFilters = USER_FILTER_KEYS.some((key) => searchParams.has(key))

    // Initial month bucket
    const now = dayjs()
    const initialBucket = createMonthBucket(now.startOf("month"), true)

    const [state, dispatch] = useReducer(reducer, {
        buckets: [initialBucket],
        isAutoFilling: !hasUserFilters,
    })

    // Sequential fetch mutex — only one request in-flight at a time (AC5)
    const fetchingRef = useRef(false)
    const abortRef = useRef(false)
    // Track consecutive empty months for stop condition
    const consecutiveEmptyRef = useRef(0)
    // Ref to access current buckets without re-creating callbacks
    const bucketsRef = useRef(state.buckets)
    bucketsRef.current = state.buckets

    // Compute trailing consecutive empty completed months from state
    let trailingEmpty = 0
    for (let i = state.buckets.length - 1; i >= 0; i -= 1) {
        const b = state.buckets[i]
        if (b.isComplete && b.data.length === 0) {
            trailingEmpty += 1
        } else {
            break
        }
    }
    consecutiveEmptyRef.current = trailingEmpty

    // ── Fetch a single page for a month ────────────────────────────────

    const fetchPage = useCallback(
        async (bucket: MonthBucket, cursor: Record<string, string> | null) => {
            // When user has filters, use the non-monthly endpoint (no date
            // range) so the API searches across all time.
            let url: string
            if (hasUserFilters) {
                url = DNO_LIST_APPLICATIONS(
                    searchParamsString,
                    APPLICATION_PAGE_SIZE,
                    ""
                )
            } else {
                // Strip updatedSince/updatedTo — the monthly endpoint
                // adds its own date range via bucket.from / bucket.to.
                const monthlyParams = new URLSearchParams(searchParamsString)
                monthlyParams.delete("updatedSince")
                monthlyParams.delete("updatedTo")
                url = DNO_LIST_APPLICATIONS_MONTHLY(
                    monthlyParams.toString(),
                    APPLICATION_PAGE_SIZE,
                    bucket.from,
                    bucket.to
                )
            }

            const body = cursor ?? null
            const res = await postClientProtected(url, body)
            return dnoApplicationsMapper.mapList(res)
        },
        [searchParamsString, hasUserFilters]
    )

    // ── Load a month — stable reference via bucketsRef ─────────────────

    const loadMonth = useCallback(
        async (monthKey: string) => {
            // Read from ref to avoid dependency on state.buckets
            const bucket = bucketsRef.current.find((b) => b.key === monthKey)
            if (!bucket || bucket.isComplete || bucket.isLoading) return
            // Sequential guard (AC5)
            if (fetchingRef.current) return
            fetchingRef.current = true

            dispatch({ type: "FETCH_START", monthKey })

            try {
                const result: IApplications = await fetchPage(
                    bucket,
                    bucket.data.length > 0 ? bucket.nextPageKey : null
                )
                if (abortRef.current) {
                    fetchingRef.current = false
                    return
                }
                dispatch({
                    type: hasUserFilters
                        ? "FETCH_SUCCESS_FILTERED"
                        : "FETCH_SUCCESS",
                    monthKey,
                    data: result.data,
                    downloadData: result.downloadData,
                    nextPageKey: result.nextPageKey ?? null,
                })
            } catch {
                dispatch({ type: "FETCH_ERROR", monthKey })
            } finally {
                fetchingRef.current = false
            }
        },
        [fetchPage, hasUserFilters]
    )

    // ── Auto-fill logic (AC1, AC2) ───────────────────────────────────

    useEffect(() => {
        if (!enabled) return
        if (!state.isAutoFilling) return
        if (fetchingRef.current) return

        const totalRecords = state.buckets.reduce(
            (sum, b) => sum + b.data.length,
            0
        )

        // Stop auto-filling once we have at least one page of records
        if (totalRecords >= APPLICATION_PAGE_SIZE) {
            dispatch({ type: "SET_AUTO_FILLING", value: false })
            return
        }

        // Find the last bucket (oldest month being processed)
        const lastBucket = state.buckets[state.buckets.length - 1]
        if (!lastBucket) return

        // If the last bucket hasn't been fetched yet, fetch it
        if (
            lastBucket.data.length === 0 &&
            !lastBucket.isComplete &&
            !lastBucket.isLoading &&
            !lastBucket.isError
        ) {
            loadMonth(lastBucket.key)
            return
        }

        // If the last bucket is still loading, wait
        if (lastBucket.isLoading) return

        // If the last bucket has an error, stop auto-filling
        if (lastBucket.isError) {
            dispatch({ type: "SET_AUTO_FILLING", value: false })
            return
        }

        // AC2: Month is complete with < 30 records — expand to previous month
        if (lastBucket.isComplete) {
            // Stop condition (consecutiveEmptyRef computed during render)
            if (consecutiveEmptyRef.current >= MAX_EMPTY_MONTHS) {
                dispatch({ type: "SET_AUTO_FILLING", value: false })
                return
            }

            // Not enough records yet, expand to previous month
            if (totalRecords < APPLICATION_PAGE_SIZE) {
                const prevMonthStart = getPreviousMonth(lastBucket.key)
                const newBucket = createMonthBucket(prevMonthStart, false)
                dispatch({ type: "ADD_MONTH", bucket: newBucket })
                return
            }

            // Enough records — stop auto-filling
            dispatch({ type: "SET_AUTO_FILLING", value: false })
        }
    }, [enabled, state.buckets, state.isAutoFilling, loadMonth])

    // ── Manual-load: handles months added by loadMore ─────────────────

    useEffect(() => {
        if (!enabled) return
        if (state.isAutoFilling) return
        if (fetchingRef.current) return

        // When filters are active, stop auto-loading once we have a full
        // page of records — further pages are loaded via explicit loadMore.
        if (hasUserFilters) {
            const totalRecords = state.buckets.reduce(
                (sum, b) => sum + b.data.length,
                0
            )
            if (totalRecords >= APPLICATION_PAGE_SIZE) return
        }

        // Load any unfetched bucket that loadMore added
        const unfetchedBucket = state.buckets.find(
            (b) =>
                b.data.length === 0 &&
                !b.isComplete &&
                !b.isLoading &&
                !b.isError
        )
        if (unfetchedBucket) {
            loadMonth(unfetchedBucket.key)
            return
        }

        // When user has filters, do NOT backfill past months
        if (hasUserFilters) return

        // Skip past empty completed months automatically
        const lastBucket = state.buckets[state.buckets.length - 1]
        if (
            lastBucket &&
            lastBucket.isComplete &&
            lastBucket.data.length === 0 &&
            consecutiveEmptyRef.current < MAX_EMPTY_MONTHS
        ) {
            const prevMonthStart = getPreviousMonth(lastBucket.key)
            const newBucket = createMonthBucket(prevMonthStart, false)
            dispatch({ type: "ADD_MONTH", bucket: newBucket })
        }
    }, [enabled, state.buckets, state.isAutoFilling, loadMonth, hasUserFilters])

    // ── Reset when filters change ──────────────────────────────────────

    const prevSearchParamsRef = useRef(searchParamsString)
    useEffect(() => {
        if (prevSearchParamsRef.current !== searchParamsString) {
            prevSearchParamsRef.current = searchParamsString
            abortRef.current = true
            fetchingRef.current = false
            consecutiveEmptyRef.current = 0

            const resetBucket = createMonthBucket(
                dayjs().startOf("month"),
                true
            )
            // When filters are cleared, re-enable auto-fill so that
            // the hook backfills previous months the same way it does
            // on initial page load.
            dispatch({
                type: hasUserFilters ? "RESET_FILTERED" : "RESET",
                initialBucket: resetBucket,
            })

            // Allow new fetches after reset
            setTimeout(() => {
                abortRef.current = false
            }, 0)
        }
    }, [searchParamsString])

    // ── Derived values ─────────────────────────────────────────────────

    // Only show buckets that actually contain data. During auto-fill,
    // empty months are probed silently — the caller can use `isAutoFilling`
    // to show a single stable skeleton instead of individual skeletons that
    // flash in/out as each empty month is checked. After auto-fill,
    // "See More" shows its own loading indicator via isLoadingAny.
    const visibleBuckets = state.buckets.filter((b) => b.data.length > 0)

    const allData = state.buckets.reduce(
        (acc: IApplicationSummary[], b) => [...acc, ...b.data],
        []
    )

    const allDownloadData = state.buckets.reduce(
        (acc: unknown[], b) => [...acc, ...b.downloadData],
        []
    )

    const isLoadingAny = state.buckets.some((b) => b.isLoading)
    const isError = state.buckets.some((b) => b.isError)
    // When filters are active, only show "See More" if there are more
    // pages to paginate (nextPageKey exists). No backward month expansion.
    const hasMorePages = state.buckets.some(
        (b) => !b.isComplete && !b.isLoading
    )
    const isFinalMonth = hasUserFilters
        ? !hasMorePages
        : consecutiveEmptyRef.current >= MAX_EMPTY_MONTHS

    const loadMore = useCallback(() => {
        const buckets = bucketsRef.current
        // If there's a bucket with more pages, continue paginating it
        const incompleteBucket = buckets.find(
            (b) => !b.isComplete && !b.isLoading
        )
        if (incompleteBucket) {
            loadMonth(incompleteBucket.key)
            return
        }
        // When user has filters, do NOT expand to previous months
        if (hasUserFilters) return
        // Otherwise expand to next (previous) month
        const lastBucket = buckets[buckets.length - 1]
        if (lastBucket) {
            const prevMonthStart = getPreviousMonth(lastBucket.key)
            const newBucket = createMonthBucket(prevMonthStart, false)
            dispatch({ type: "ADD_MONTH", bucket: newBucket })
        }
    }, [loadMonth, hasUserFilters])

    const reset = useCallback(() => {
        abortRef.current = true
        fetchingRef.current = false
        consecutiveEmptyRef.current = 0
        const resetBucket = createMonthBucket(dayjs().startOf("month"), true)
        dispatch({ type: "RESET", initialBucket: resetBucket })
        setTimeout(() => {
            abortRef.current = false
        }, 0)
    }, [])

    return {
        monthBuckets: visibleBuckets,
        allData,
        downloadData: { results: allDownloadData },
        isLoadingAny,
        isAutoFilling: state.isAutoFilling,
        isError,
        isFinalMonth,
        loadMonth,
        loadMore,
        reset,
    }
}

export default useGetDnoApplicationsMonthly
export { reducer, createMonthBucket, getPreviousMonth, MAX_EMPTY_MONTHS }
